import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useBootDeps } from "../lib/boot/deps";
import { BreakReminder } from "../features/pico";
import { TopBarCtx } from "../components/TopBar";
import type { TopBarSpec } from "../components/TopBar";
import { useDensity } from "../store/density";
import { useT } from "../i18n";
import { useSession } from "../store/session/SessionProvider";
import { useTelemetrySender } from "../hooks/useTelemetry";

/**
 * 상단 바 — 학생: Campus 밝은 초록 · 교사: 짙은 초록(SCR v0.6 FS-01 / FS-01-04 와이어).
 * 제목·칩은 화면이 useTopBar 로 정한다. 학생은 제목 뒤에 밀도 칩(SE 모드 · SU 모드)이 붙는다.
 * 표시 이름은 와이어에 없으므로 화면에 그리지 않고 스크린 리더에만 알린다(작품명·피코 인사에 별명이 보인다).
 * 개발용 SE/SU 전환은 상단 바가 아니라 가짜 모드의 개발 도크(BootDeps.devDock)에 있다.
 */
/** 놀이터 입구 단추를 두지 않는 경로 — 입구 자체 · 입장(문 A LTI · 문 B SSO) */
const NO_HOME_LINK = new Set(["/", "/home", "/launch", "/sso"]);

export function AppLayout() {
  const t = useT();
  const { density } = useDensity();
  const deps = useBootDeps();
  const state = useSession();
  const [spec, setSpec] = useState<TopBarSpec | null>(null);
  // S5 계측(POST /api/telemetry) — 세션이 서면 보낸다
  useTelemetrySender();
  const session = state.status === "ready" ? state.session : null;
  // 운영자(FS-15) · 기관 담당자(FS-15-06)도 교사와 같은 PC 틀(짙은 상단 바 · 밀도 칩 · 휴식 안내 없음)
  // 관찰자(P3-S5 보호자)도 어른 틀 — 세션이 없을 때는 화면이 frame: "adult" 로 정한다(와이어 sfs-01-05 top t2)
  const teacher =
    session?.role === "teacher" ||
    session?.role === "operator" ||
    session?.role === "org_admin" ||
    session?.role === "observer" ||
    spec?.frame === "adult";

  // 학생 화면 어디서든 놀이터 입구(FS-01-02 · /home)로 — 입구 자체 · 입장(문 A/B) 화면에는 두지 않는다(2026-10-01 요청)
  const { pathname } = useLocation();
  const homeLink =
    !!session && !teacher && session.role === "student" && !NO_HOME_LINK.has(pathname);
  const icon = spec?.icon ?? (teacher ? "👩‍🏫" : "🌿");
  const title = spec?.title ?? (teacher ? t("teacher.home.appTitle") : t("common.appName"));

  return (
    <TopBarCtx.Provider value={setSpec}>
      <div className={`pf-app${teacher ? " pf-app--teacher" : ""}`}>
        <header className={`pf-top${teacher ? " pf-top--teacher" : ""}`} data-testid="top-bar">
          {homeLink && (
            <Link to="/home" className="pf-top__home" data-testid="top-home">
              <span aria-hidden="true">🏠</span> {t("common.toHome")}
            </Link>
          )}
          <span className="pf-top__mark" aria-hidden="true">
            {icon}
          </span>
          <span className="pf-top__title" data-testid="top-title">
            {title}
          </span>
          {session && (
            <span className="pf-sr-only" data-testid="display-name">
              {session.displayName}
            </span>
          )}
          {session && !teacher && (
            <span className="pf-top__chip" data-testid="density-chip">
              {t(density === "se" ? "common.densitySE" : "common.densitySU")}
            </span>
          )}
          {spec?.chips?.map((c) => (
            <span
              key={c.label}
              className={`pf-top__chip${c.big ? " pf-top__chip--big" : ""}`}
              data-testid={c.testId}
            >
              {c.label}
            </span>
          ))}
        </header>
        <main className="pf-main" id="main">
          <Outlet />
        </main>
        {session && !teacher && <BreakReminder afterMs={deps.breakAfterMs} />}
        {deps.devDock}
      </div>
    </TopBarCtx.Provider>
  );
}
